import {HowToVideo} from '../../services/cellcast.type'

/**
 * Utility functions for video management
 */

/**
 * Format video duration from seconds to readable format
 */
export const formatDuration = (seconds: number): string => {
  if (!seconds) return '0:00'

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

/**
 * Get video thumbnail URL with fallback
 */
export const getVideoThumbnail = (video: HowToVideo): string => {
  return (
    video.imagePath ||
    'https://cel-public-assets.s3.ap-southeast-2.amazonaws.com/cel_coming_soon.png'
  )
}

/**
 * Check if video has valid URL
 */
export const hasValidVideoUrl = (video: HowToVideo): boolean => {
  return !!(video.videoUrl && video.videoUrl.trim().length > 0)
}

/**
 * Extract video ID from various video platforms
 */
export const extractVideoId = (url: string): string | null => {
  if (!url) return null

  // Loom video ID extraction
  const loomMatch = url.match(/loom\.com\/(?:embed|share)\/([a-zA-Z0-9]+)/)
  if (loomMatch) return loomMatch[1]

  // YouTube video ID extraction
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)
  if (youtubeMatch) return youtubeMatch[1]

  // Vimeo video ID extraction
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)
  if (vimeoMatch) return vimeoMatch[1]

  return null
}

/**
 * Get video platform from URL
 */
export const getVideoPlatform = (url: string): 'loom' | 'youtube' | 'vimeo' | 'other' => {
  if (!url) return 'other'

  if (url.includes('loom.com')) return 'loom'
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube'
  if (url.includes('vimeo.com')) return 'vimeo'

  return 'other'
}

/**
 * Generate embed URL from video URL
 */
export const generateEmbedUrl = (url: string): string => {
  if (!url) return ''

  const platform = getVideoPlatform(url)
  const videoId = extractVideoId(url)

  if (!videoId) return url

  switch (platform) {
    case 'loom':
      return `https://www.loom.com/embed/${videoId}`
    case 'youtube':
      return `https://www.youtube.com/embed/${videoId}`
    case 'vimeo':
      return `https://player.vimeo.com/video/${videoId}`
    default:
      return url
  }
}

/**
 * Validate video URL format
 */
export const isValidVideoUrl = (url: string): boolean => {
  if (!url) return false

  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Get video category display name
 */
export const getCategoryDisplayName = (category?: string): string => {
  if (!category) return 'General'

  const categoryMap: Record<string, string> = {
    'getting-started': 'Getting Started',
    messaging: 'Messaging',
    automation: 'Automation',
    contacts: 'Contacts',
    reports: 'Reports',
    integrations: 'Integrations',
    advanced: 'Advanced',
  }

  return categoryMap[category] || category
}

/**
 * Sort videos by various criteria
 */
export const sortVideos = (
  videos: HowToVideo[],
  sortBy: 'title' | 'createdAt' | 'sortOrder' = 'sortOrder',
  order: 'asc' | 'desc' = 'asc'
): HowToVideo[] => {
  return [...videos].sort((a, b) => {
    let aValue: any
    let bValue: any

    // Handle different sort fields
    switch (sortBy) {
      case 'title':
        aValue = a.title || ''
        bValue = b.title || ''
        break
      case 'createdAt':
        aValue = a.createdAt || ''
        bValue = b.createdAt || ''
        break
      case 'sortOrder':
        aValue = a.sortOrder || 0
        bValue = b.sortOrder || 0
        break
      default:
        aValue = 0
        bValue = 0
    }

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }

    if (order === 'desc') {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
    } else {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
    }
  })
}

/**
 * Filter videos by search term
 */
export const filterVideosBySearch = (videos: HowToVideo[], searchTerm: string): HowToVideo[] => {
  if (!searchTerm.trim()) return videos

  const term = searchTerm.toLowerCase().trim()

  return videos.filter(
    (video) =>
      video.title.toLowerCase().includes(term) ||
      video.description.toLowerCase().includes(term) ||
      video.subTitle?.toLowerCase().includes(term) ||
      video.subDescription?.toLowerCase().includes(term) ||
      video.category?.toLowerCase().includes(term) ||
      video.tags?.some((tag) => tag.toLowerCase().includes(term))
  )
}
